export type Language = 'ru' | 'en';

export const translations = {
    ru: {
        hero: {
            title: "Lingwise — учи слова без лишних усилий",
            subtitle: "Помогает выбрать нужные слова и подсказывает, когда к ним лучше вернуться.",
            cta: "Попробовать в Telegram",
        },
        wordsFoundation: {
            title: "Слова — основа языка",
            description: "Какой бы ни была цель — чтение, работа, учёба или общение — она всегда упирается в словарный запас.",
            highlight: "Слова определяют, насколько уверенно вы понимаете и выражаете мысли.",
        },
        regularWork: {
            title: "Без регулярной работы прогресса не будет",
            description: "Слова нужно не просто замечать — к ним нужно возвращаться со временем.",
            challenge: "Самостоятельно выстроить такой процесс сложно: нужно понимать, какие слова учить, когда к ним возвращаться и как не перегружать себя повторениями.",
        },
        organization: {
            title: "Lingwise берёт организацию на себя",
            description: "Процесс уже продуман и проверен — вам не нужно тратить время на то, чтобы выстраивать систему.",
            timing: "Чтобы слова запоминались, к ним важно возвращаться в нужный момент. Lingwise сам определяет этот момент и предлагает повторения вовремя.",
            adaptation: "Если со словом идёт легко — оно будет появляться реже. Если слово даётся с трудом — чаще, пока оно не закрепится.",
        },
        process: {
            title: "Как выглядит процесс",
            steps: [
                {
                    title: "Определяем уровень",
                    description: "В начале — короткий тест, чтобы понять текущий уровень.",
                },
                {
                    title: "Подбираем слова",
                    description: "Lingwise предлагает слова, подходящие для вашего уровня. Если вы укажете цели и интересы, подбор станет точнее.",
                },
                {
                    title: "Вы выбираете, с чем работать",
                    description: "Можно добавить слово в обучение, пропустить его или отметить как уже знакомое.",
                },
                {
                    title: "Переходите к практике",
                    description: "Когда слова добавлены, начинаются сессии повторения. Сначала Lingwise показывает варианты перевода — нужно выбрать правильный. Позже слово нужно вспомнить самостоятельно.",
                },
                {
                    title: "Повторения адаптируются",
                    description: "Каждое успешное повторение повышает уровень слова. Чем выше уровень, тем реже оно появляется в практике.",
                },
                {
                    title: "Дополнительная поддержка",
                    description: "Для всех слов доступны произношение и примеры использования в предложениях.",
                },
            ],
        },
        progress: {
            title: "Контроль прогресса и сложные слова",
            description: "В Lingwise можно посмотреть статистику по повторениям: что вы уже хорошо знаете и с какими словами возникают трудности.",
            difficult: "Отдельно можно поработать с «крепкими орешками» — словами, которые даются особенно тяжело.",
        },
        texts: {
            title: "Работа с текстами",
            description: "Можно работать с реальными материалами — статьями, абзацами или фрагментами книг.",
            benefit: "Достаточно отправить текст, и Lingwise выделит слова, которые могут помешать пониманию, и предложит разобрать их заранее. Это упрощает чтение и помогает быстрее ориентироваться в новых текстах.",
        },
        changes: {
            title: "Что меняется со временем",
            description: "Становится понятно:",
            points: [
                "с какими словами вы работаете сейчас",
                "что уже закрепилось",
                "к чему можно вернуться позже",
            ],
            result: "Словарный запас перестаёт быть абстрактным. Он становится наблюдаемым и управляемым ресурсом.",
            understanding: "Появляется понимание, где вы уверены, а где стоит приложить усилия, и инструмент для целенаправленной работы с этим.",
            active: "Слова постепенно переходят в активный запас.",
        },
        important: {
            title: "Важно уточнить",
            description: "Lingwise не учит язык за вас. Он помогает организовать работу со словами и вовремя к ним возвращаться.",
        },
        finalCta: {
            title: "Начать просто",
            description: "Без настройки. С одного слова.",
            button: "Запустить Lingwise в Telegram",
        },
        terms: {
            title: "Пользовательское соглашение Lingwise",
            lastUpdated: "Дата последнего обновления: 14 января 2026 года",
            content: [
                "Настоящее Пользовательское соглашение применяется ко всем Пользователям Сервиса независимо от способа доступа (включая веб-сайт и Telegram) и определяет условия использования Lingwise с учётом страны проживания Пользователя и используемого способа оплаты.",
                "Начало использования Сервиса, включая просмотр функционала, отправку сообщений, оформление подписки или совершение оплаты, означает полное и безоговорочное принятие (акцепт) Пользователем условий настоящего Соглашения.",
                "Настоящее Пользовательское соглашение определяет условия использования Telegram‑приложения Lingwise и является юридически обязательным договором между пользователем и правообладателем сервиса.",
            ],
            sections: [
                {
                    title: "1. Термины и общие положения",
                    items: [
                        {
                            title: "1.1.",
                            content: "Правообладателем Сервиса является Lingwise (далее — «Правообладатель»), действующий в соответствии с применимым законодательством Российской Федерации.",
                        },
                        {
                            title: "1.21.",
                            content: "Lingwise — цифровой информационно‑образовательный сервис, функционирующий на платформе Telegram, включая бота, связанные программные модули и интерфейсы.",
                        },
                        {
                            title: "1.3.",
                            content: "Пользователь — физическое лицо, получающее доступ к Сервису посредством учетной записи Telegram.",
                        },
                        {
                            title: "1.4.",
                            content: "Используя Lingwise, Пользователь подтверждает, что полностью ознакомился с настоящим Соглашением, понимает его условия и принимает их в полном объеме без каких-либо оговорок.",
                        },
                        {
                            title: "1.5.",
                            content: "В случае несогласия с условиями Соглашения Пользователь обязан прекратить использование Сервиса.",
                        },
                    ],
                },
                {
                    title: "2. Предмет соглашения",
                    items: [
                        {
                            title: "2.1.",
                            content: "Lingwise предоставляет Пользователю право использования Сервиса в целях изучения и практики иностранных языков, а также доступа к сопутствующим обучающим материалам и функциям.",
                        },
                        {
                            title: "2.2.",
                            content: "Сервис предоставляется на условиях простой (неисключительной) лицензии.",
                        },
                        {
                            title: "2.3.",
                            content: "Lingwise вправе по своему усмотрению изменять функциональные возможности Сервиса, его структуру, алгоритмы и интерфейс без предварительного уведомления Пользователя, в том числе с возможной временной недоступностью отдельных функций или Сервиса в целом в связи с обновлениями, техническими работами или улучшением функционала.",
                        },
                    ],
                },
                {
                    title: "3. Условия использования",
                    items: [
                        {
                            title: "3.1.",
                            content: "Пользователь обязуется использовать Сервис исключительно в законных целях и способами, не нарушающими права и законные интересы третьих лиц.",
                        },
                        {
                            title: "3.2.",
                            content: "Пользователю запрещается:",
                            subitems: [
                                "вмешиваться в работу Сервиса или предпринимать попытки обхода технических ограничений;",
                                "использовать Lingwise для распространения запрещенной информации;",
                                "совершать действия, способные привести к нарушению стабильной работы Сервиса.",
                            ],
                        },
                        {
                            title: "3.3.",
                            content: "Все действия, совершенные через учетную запись Telegram Пользователя, считаются совершенными самим Пользователем.",
                        },
                    ],
                },
                {
                    title: "4. Регистрация и доступ",
                    items: [
                        {
                            title: "4.1.",
                            content: "Доступ к Lingwise осуществляется посредством учетной записи Telegram.",
                        },
                        {
                            title: "4.2.",
                            content: "Lingwise не осуществляет проверку личности Пользователя и не несет ответственности за достоверность данных, указанных в профиле Telegram.",
                        },
                        {
                            title: "4.3.",
                            content: "Пользователь самостоятельно обеспечивает безопасность доступа к своему аккаунту Telegram.",
                        },
                    ],
                },
                {
                    title: "5. Интеллектуальная собственность",
                    items: [
                        {
                            title: "5.1.",
                            content: "Все элементы Сервиса, включая тексты, методики, программный код, базы данных и дизайн, являются объектами интеллектуальной собственности Lingwise и охраняются в соответствии с законодательством Российской Федерации.",
                        },
                        {
                            title: "5.2.",
                            content: "Пользователю не передаются исключительные права на какие-либо элементы Сервиса.",
                        },
                    ],
                },
                {
                    title: "6. Пользовательский контент",
                    items: [
                        {
                            title: "6.1.",
                            content: "Пользователь вправе направлять в Lingwise текстовые сообщения и иные данные в рамках функционала Сервиса.",
                        },
                        {
                            title: "6.2.",
                            content: "Направляя такой контент, Пользователь предоставляет Lingwise право безвозмездного использования, обработки и хранения данного контента в целях функционирования и улучшения Сервиса.",
                        },
                        {
                            title: "6.3.",
                            content: "Пользователь гарантирует, что направляемый контент не нарушает требований законодательства и прав третьих лиц.",
                        },
                    ],
                },
                {
                    title: "7. Платные услуги, платежи и возвраты",
                    items: [
                        {
                            title: "7.1.",
                            content: "Lingwise вправе предоставлять отдельные функции и возможности Сервиса на возмездной основе.",
                        },
                        {
                            title: "7.2.",
                            content: "Способы оплаты, доступные Пользователю, определяются Правообладателем и могут изменяться без предварительного уведомления.",
                        },
                        {
                            title: "7.3.",
                            content: "Для пользователей, находящихся на территории Российской Федерации, оплата платных функций осуществляется Пользователем напрямую Правообладателю с использованием сторонних платёжных сервисов, включая, но не ограничиваясь, ЮKassa. В данном случае Правообладатель является продавцом цифрового продукта.",
                        },
                        {
                            title: "7.4.",
                            content: "Для пользователей, находящихся за пределами Российской Федерации, оплата может обрабатываться компанией Paddle.com либо иным аналогичным платёжным провайдером, выступающим в качестве Merchant of Record. В таком случае соответствующий платёжный провайдер является стороной договора купли-продажи цифрового продукта, а также самостоятельно определяет порядок возвратов и налогообложения.",
                        },
                        {
                            title: "7.5.",
                            content: "Подписки, если иное не указано при оплате, считаются продлёнными автоматически до момента их отмены Пользователем.",
                        },
                        {
                            title: "7.6.",
                            content: "Денежные средства, уплаченные за доступ к цифровому функционалу Сервиса, возврату не подлежат, за исключением случаев, прямо предусмотренных обязательным законодательством либо правилами применимого платёжного провайдера.",
                        },
                    ],
                },
                {
                    title: "8. Ответственность сторон",
                    items: [
                        {
                            title: "8.1.",
                            content: "Lingwise не несет ответственности за невозможность использования Сервиса по причинам, не зависящим от него, включая сбои или ограничения в работе Telegram, сторонних сервисов, платформ, API, платежных провайдеров, сетей связи, хостинг-инфраструктуры, а также сети Интернет или устройств Пользователя.",
                        },
                        {
                            title: "8.2.",
                            content: "Сервис предоставляется по принципу «как есть» и «как доступно», без каких-либо гарантий соответствия ожиданиям Пользователя, включая гарантии непрерывной, бесперебойной или безошибочной работы Сервиса.",
                        },
                        {
                            title: "8.3.",
                            content: "Lingwise вправе приостанавливать работу Сервиса полностью или частично для проведения технического обслуживания, обновлений или иных работ без возникновения ответственности перед Пользователем.",
                        },
                        {
                            title: "8.4.",
                            content: "Совокупная ответственность Lingwise в любом случае ограничивается суммой, фактически уплаченной Пользователем за последние 12 месяцев использования платных функций.",
                        },
                    ],
                },
                {
                    title: "9. Возмещение убытков",
                    items: [
                        {
                            title: "9.1.",
                            content: "Пользователь обязуется самостоятельно урегулировать и компенсировать претензии третьих лиц, возникшие вследствие нарушения Пользователем условий настоящего Соглашения.",
                        },
                    ],
                },
                {
                    title: "10. Ссылки и сторонние сервисы",
                    items: [
                        {
                            title: "10.1.",
                            content: "Lingwise может содержать ссылки или упоминания сторонних ресурсов. Lingwise не контролирует и не отвечает за их содержание и работу.",
                        },
                    ],
                },
                {
                    title: "11. Прекращение доступа",
                    items: [
                        {
                            title: "11.1.",
                            content: "Lingwise вправе в одностороннем порядке ограничить или прекратить доступ Пользователя к Сервису при выявлении нарушений настоящего Соглашения.",
                        },
                        {
                            title: "11.2.",
                            content: "Прекращение доступа не освобождает Пользователя от обязательств, возникших ранее.",
                        },
                    ],
                },
                {
                    title: "12. Конфиденциальность",
                    items: [
                        {
                            title: "12.1.",
                            content: "Обработка информации осуществляется в соответствии с Политикой конфиденциальности Lingwise.",
                        },
                    ],
                },
                {
                    title: "13. Разрешение споров",
                    items: [
                        {
                            title: "13.1.",
                            content: "Все споры и разногласия подлежат разрешению путем переговоров.",
                        },
                        {
                            title: "13.2.",
                            content: "При недостижении соглашения спор подлежит рассмотрению в порядке, установленном действующим законодательством Российской Федерации.",
                        },
                    ],
                },
                {
                    title: "14. Заключительные положения",
                    items: [
                        {
                            title: "14.1.",
                            content: "Настоящее Соглашение является полным и исчерпывающим договором между Пользователем и Lingwise.",
                        },
                        {
                            title: "14.2.",
                            content: "Lingwise вправе передавать свои права и обязанности по Соглашению третьим лицам.",
                        },
                        {
                            title: "14.3.",
                            content: "К настоящему Соглашению применяется право Российской Федерации.",
                        },
                    ],
                },
            ],
        },
        privacy: {
            title: "Политика конфиденциальности Lingwise",
            lastUpdated: "Дата последнего обновления: 14 января 2026 года",
            content: [],
            sections: [
                {
                    title: "1. Общие положения",
                    items: [
                        {
                            title: "",
                            content: "Настоящая Политика конфиденциальности описывает порядок обработки информации о пользователях сервиса Lingwise, доступного через веб-сайт и Telegram (далее — «Сервис»).",
                        },
                        {
                            title: "",
                            content: "Используя Сервис, Пользователь подтверждает свое согласие с условиями настоящей Политики конфиденциальности и дает согласие на обработку информации в соответствии с изложенными ниже условиями.",
                        },
                        {
                            title: "",
                            content: "Отдельные функции Сервиса могут регулироваться дополнительными условиями конфиденциальности, о которых Пользователь уведомляется при использовании таких функций.",
                        },
                    ],
                },
                {
                    title: "2. Объем обрабатываемой информации",
                    items: [
                        {
                            title: "",
                            content: "Lingwise не осуществляет сбор персональных данных сверх тех, которые:",
                            subitems: [
                                "передаются Пользователем через платформу Telegram;",
                                "предоставляются платежными провайдерами при оформлении платных функций;",
                                "автоматически формируются в процессе использования Сервиса (техническая и статистическая информация).",
                            ],
                        },
                        {
                            title: "",
                            content: "Lingwise не запрашивает у Пользователей специальные категории персональных данных.",
                        },
                    ],
                },
                {
                    title: "3. Цели и основания обработки",
                    items: [
                        {
                            title: "",
                            content: "Обработка информации осуществляется на следующих правовых основаниях:",
                            subitems: [
                                "исполнение пользовательского соглашения;",
                                "законные интересы Lingwise, направленные на развитие и защиту Сервиса;",
                                "соблюдение требований применимого законодательства;",
                                "согласие Пользователя, когда оно требуется.",
                            ],
                        },
                        {
                            title: "",
                            content: "В частности, информация может использоваться для:",
                            subitems: [
                                "обеспечения работы и улучшения Сервиса;",
                                "персонализации учебного контента и сохранения прогресса;",
                                "технической поддержки и обратной связи;",
                                "аналитики, тестирования и повышения качества функционала;",
                                "предотвращения нарушений и злоупотреблений.",
                            ],
                        },
                    ],
                },
                {
                    title: "4. Передача информации третьим лицам",
                    items: [
                        {
                            title: "",
                            content: "Lingwise вправе привлекать третьих лиц для обеспечения работы Сервиса, включая, но не ограничиваясь:",
                            subitems: [
                                "хостинг-провайдеров;",
                                "аналитические и технические сервисы;",
                                "платежные системы и процессинговые компании;",
                                "поставщиков технологий искусственного интеллекта и автоматизации.",
                            ],
                        },
                        {
                            title: "",
                            content: "Передача информации осуществляется в объеме, необходимом для достижения указанных целей.",
                        },
                        {
                            title: "",
                            content: "При использовании платных функций обработка платежной информации осуществляется соответствующим платёжным провайдером в соответствии с его собственной политикой конфиденциальности.",
                        },
                    ],
                },
                {
                    title: "5. Коммуникации",
                    items: [
                        {
                            title: "",
                            content: "Lingwise вправе направлять Пользователю сообщения, связанные с:",
                            subitems: [
                                "работой Сервиса;",
                                "обучающим процессом и прогрессом;",
                                "изменениями функционала или условий использования;",
                                "новыми возможностями и предложениями.",
                            ],
                        },
                        {
                            title: "",
                            content: "Пользователь может отказаться от получения необязательных уведомлений, если это предусмотрено настройками используемой платформы.",
                        },
                    ],
                },
                {
                    title: "6. Соблюдение закона и защита прав",
                    items: [
                        {
                            title: "",
                            content: "Информация может быть раскрыта в случаях, когда это необходимо:",
                            subitems: [
                                "для исполнения законных требований государственных органов;",
                                "для защиты прав и законных интересов Lingwise или третьих лиц;",
                                "для предотвращения мошенничества, нарушений или причинения вреда.",
                            ],
                        },
                    ],
                },
                {
                    title: "7. Обезличенные данные",
                    items: [
                        {
                            title: "",
                            content: "Lingwise вправе использовать агрегированную и обезличенную информацию без ограничений. Такая информация не позволяет идентифицировать Пользователя и не относится к персональным данным.",
                        },
                    ],
                },
                {
                    title: "8. Права пользователя",
                    items: [
                        {
                            title: "",
                            content: "Пользователь вправе в пределах, установленных применимым законодательством:",
                            subitems: [
                                "получать сведения об обрабатываемой информации;",
                                "требовать уточнения или удаления данных;",
                                "ограничивать или возражать против обработки;",
                                "отзывать ранее предоставленное согласие;",
                                "запрашивать переносимость данных.",
                            ],
                        },
                        {
                            title: "",
                            content: "Запросы могут быть отклонены в случаях, предусмотренных законом, включая невозможность идентификации Пользователя или необходимость сохранения информации для законных целей.",
                        },
                    ],
                },
                {
                    title: "9. Сроки хранения",
                    items: [
                        {
                            title: "",
                            content: "Информация хранится в течение срока использования Сервиса и может быть сохранена дольше, если это необходимо для:",
                            subitems: [
                                "исполнения юридических обязанностей;",
                                "защиты прав и интересов Lingwise;",
                                "разрешения споров;",
                                "обеспечения безопасности и стабильности Сервиса.",
                            ],
                        },
                        {
                            title: "",
                            content: "Обезличенные данные могут храниться без ограничения по сроку.",
                        },
                    ],
                },
                {
                    title: "10. Трансграничная обработка",
                    items: [
                        {
                            title: "",
                            content: "Lingwise может осуществлять обработку информации с использованием инфраструктуры, расположенной в различных юрисдикциях, при условии соблюдения применимых требований законодательства о защите данных.",
                        },
                    ],
                },
                {
                    title: "11. Сторонние ресурсы",
                    items: [
                        {
                            title: "",
                            content: "Сервис может содержать ссылки на сторонние сайты или сервисы. Lingwise не контролирует их политику конфиденциальности и не несет ответственности за обработку информации такими третьими лицами.",
                        },
                    ],
                },
                {
                    title: "12. Обновление политики",
                    items: [
                        {
                            title: "",
                            content: "Lingwise вправе изменять настоящую Политику конфиденциальности. Актуальная версия всегда доступна на сайте Сервиса. Существенные изменения вступают в силу по истечении разумного срока с момента публикации.",
                        },
                    ],
                },
                {
                    title: "13. Контактная информация",
                    items: [
                        {
                            title: "",
                            content: "По вопросам, связанным с обработкой информации и конфиденциальностью, Пользователь может связаться с Lingwise по адресу электронной почты:",
                        },
                        {
                            title: "",
                            content: "lingwise@gmail.com",
                        },
                        {
                            title: "",
                            content: "Лицо, осуществляющее обработку персональных данных (оператор персональных данных):",
                        },
                        {
                            title: "",
                            content: "Эндюськин Вячеслав Николаевич, самозанятый (налог на профессиональный доход), ИНН 732707619372.",
                        },
                    ],
                },
            ],
        },
        pricing: {
            title: "Тарифы",
            description: "Lingwise работает по подписке. Вы можете начать бесплатно.",
            free: {
                title: "Бесплатно",
                price: "$0",
                description: "Начните и сформируйте привычку.",
                features: [
                    "До 100 слов в активном обучении",
                    "Адаптивные сессии практики",
                    "Произношение + примеры предложений",
                    "Базовый обзор прогресса",
                ],
                cta: "Начать бесплатно",
            },
            paid: {
                title: "Платно",
                monthly: "Месяц",
                yearly: "Год",
                priceMonthly: "$4.99",
                priceYearly: "$39",
                perMonth: "/ месяц",
                perYear: "/ год",
                yearlyNote: "Годовая подписка выгоднее, если вы используете Lingwise регулярно.",
                description: "Для долгосрочной, целенаправленной работы.",
                features: [
                    "Неограниченное количество слов в активном обучении",
                    "Всё из бесплатного тарифа",
                    "Полная статистика прогресса",
                    "Режим «сложных слов» — фокус на трудных словах",
                    "Режим работы с текстами — отправьте текст и извлеките слова для изучения",
                ],
                cta: "Перейти на платный",
            },
            faq: {
                title: "Часто задаваемые вопросы",
                items: [
                    {
                        question: "Можно ли отменить в любой момент?",
                        answer: "Да. Вы можете отменить подписку в любой момент.",
                    },
                    {
                        question: "Есть ли обязательства?",
                        answer: "Нет обязательств.",
                    },
                    {
                        question: "Можно ли попробовать перед оплатой?",
                        answer: "Да, доступен бесплатный тариф.",
                    },
                ],
            },
        },
    },
    en: {
        hero: {
            title: "Lingwise — learn words without extra effort",
            subtitle: "Helps you choose the right words and reminds you when it's best to return to them.",
            cta: "Try in Telegram",
        },
        wordsFoundation: {
            title: "Words are the foundation of language",
            description: "Whatever your goal — reading, work, study, or communication — it always comes down to vocabulary.",
            highlight: "Words determine how confidently you understand and express thoughts.",
        },
        regularWork: {
            title: "Without regular work, there will be no progress",
            description: "Words need more than just noticing — you need to return to them over time.",
            challenge: "Building such a process on your own is difficult: you need to understand which words to learn, when to return to them, and how not to overload yourself with repetitions.",
        },
        organization: {
            title: "Lingwise takes care of organization",
            description: "The process is already thought out and tested — you don't need to spend time building a system.",
            timing: "For words to be remembered, it's important to return to them at the right moment. Lingwise determines this moment itself and offers repetitions on time.",
            adaptation: "If a word comes easily — it will appear less often. If a word is difficult — more often, until it's mastered.",
        },
        process: {
            title: "How the process looks",
            steps: [
                {
                    title: "Determine level",
                    description: "At the start — a short test to understand your current level.",
                },
                {
                    title: "Select words",
                    description: "Lingwise suggests words suitable for your level. If you specify goals and interests, the selection becomes more accurate.",
                },
                {
                    title: "You choose what to work with",
                    description: "You can add a word to learning, skip it, or mark it as already familiar.",
                },
                {
                    title: "Move to practice",
                    description: "When words are added, repetition sessions begin. First, Lingwise shows translation options — you need to choose the correct one. Later, you need to recall the word yourself.",
                },
                {
                    title: "Repetitions adapt",
                    description: "Each successful repetition increases the word's level. The higher the level, the less often it appears in practice.",
                },
                {
                    title: "Additional support",
                    description: "Pronunciation and usage examples in sentences are available for all words.",
                },
            ],
        },
        progress: {
            title: "Progress tracking and difficult words",
            description: "In Lingwise, you can view statistics on repetitions: what you already know well and which words cause difficulties.",
            difficult: "You can separately work with 'tough nuts' — words that are especially difficult.",
        },
        texts: {
            title: "Working with texts",
            description: "You can work with real materials — articles, paragraphs, or book excerpts.",
            benefit: "Just send the text, and Lingwise will highlight words that might hinder understanding and suggest working through them in advance. This simplifies reading and helps you navigate new texts faster.",
        },
        changes: {
            title: "What changes over time",
            description: "It becomes clear:",
            points: [
                "which words you're working with now",
                "what has already been mastered",
                "what you can return to later",
            ],
            result: "Vocabulary stops being abstract. It becomes an observable and manageable resource.",
            understanding: "Understanding emerges: where you're confident and where you should put in effort, and a tool for targeted work with this.",
            active: "Words gradually move into active vocabulary.",
        },
        important: {
            title: "Important to clarify",
            description: "Lingwise doesn't teach you the language. It helps organize work with words and return to them on time.",
        },
        finalCta: {
            title: "Getting started is simple",
            description: "No setup. Start with one word.",
            button: "Launch Lingwise in Telegram",
        },
        terms: {
            title: "Lingwise Terms of Service",
            lastUpdated: "Last updated: January 14, 2026",
            content: [
                "This Terms of Service applies to all users of the Lingwise Service regardless of the method of access (including the website and Telegram) and governs the use of Lingwise taking into account the user's country of residence and the payment method used.",
                "By starting to use the Service, including browsing functionality, sending messages, subscribing, or making a payment, the user fully and unconditionally accepts these Terms.",
                "These Terms constitute a legally binding agreement between the user and the service owner.",
            ],
            sections: [
                {
                    title: "1. Definitions and General Provisions",
                    items: [
                        {
                            title: "1.1.",
                            content: "The owner of the Service is Lingwise (the \"Service Owner\"), acting in accordance with the applicable laws of the Russian Federation.",
                        },
                        {
                            title: "1.2.",
                            content: "Lingwise is a digital educational service operating on the Telegram platform, including a bot, related software modules, and interfaces.",
                        },
                        {
                            title: "1.3.",
                            content: "User means an individual accessing the Service through a Telegram account.",
                        },
                        {
                            title: "1.4.",
                            content: "By using Lingwise, the User confirms that they have reviewed these Terms, understand them, and accept them in full without any reservations.",
                        },
                        {
                            title: "1.5.",
                            content: "If the User does not agree with these Terms, they must stop using the Service.",
                        },
                    ],
                },
                {
                    title: "2. Subject of the Agreement",
                    items: [
                        {
                            title: "2.1.",
                            content: "Lingwise grants the User the right to use the Service for learning and practicing foreign languages, as well as for accessing related educational materials and features.",
                        },
                        {
                            title: "2.2.",
                            content: "The Service is provided under a simple (non-exclusive) license.",
                        },
                        {
                            title: "2.3.",
                            content: "Lingwise may, at its sole discretion, modify the functionality of the Service, its structure, algorithms, or interface without prior notice, including temporary unavailability of certain features or the Service as a whole due to updates, maintenance, or improvements.",
                        },
                    ],
                },
                {
                    title: "3. Conditions of Use",
                    items: [
                        {
                            title: "3.1.",
                            content: "The User undertakes to use the Service only for lawful purposes and in ways that do not violate the rights or legitimate interests of third parties.",
                        },
                        {
                            title: "3.2.",
                            content: "The User is prohibited from:",
                            subitems: [
                                "interfering with the operation of the Service or attempting to bypass technical restrictions;",
                                "using Lingwise to distribute prohibited or unlawful information;",
                                "performing actions that may disrupt the stability or proper functioning of the Service.",
                            ],
                        },
                        {
                            title: "3.3.",
                            content: "All actions performed through the User's Telegram account are deemed to have been performed by the User.",
                        },
                    ],
                },
                {
                    title: "4. Registration and Access",
                    items: [
                        {
                            title: "4.1.",
                            content: "Access to Lingwise is provided through a Telegram account.",
                        },
                        {
                            title: "4.2.",
                            content: "Lingwise does not verify the identity of Users and is not responsible for the accuracy of information contained in Telegram profiles.",
                        },
                        {
                            title: "4.3.",
                            content: "The User is solely responsible for maintaining the security of access to their Telegram account.",
                        },
                    ],
                },
                {
                    title: "5. Intellectual Property",
                    items: [
                        {
                            title: "5.1.",
                            content: "All elements of the Service, including texts, methodologies, software code, databases, and design, are intellectual property of Lingwise and are protected under applicable law.",
                        },
                        {
                            title: "5.2.",
                            content: "No exclusive intellectual property rights are transferred to the User.",
                        },
                    ],
                },
                {
                    title: "6. User Content",
                    items: [
                        {
                            title: "6.1.",
                            content: "The User may submit text messages and other data within the functionality of the Service.",
                        },
                        {
                            title: "6.2.",
                            content: "By submitting such content, the User grants Lingwise a royalty-free right to use, process, and store such content for the purposes of operating and improving the Service.",
                        },
                        {
                            title: "6.3.",
                            content: "The User warrants that the submitted content does not violate applicable law or the rights of third parties.",
                        },
                    ],
                },
                {
                    title: "7. Paid Services, Payments, and Refunds",
                    items: [
                        {
                            title: "7.1.",
                            content: "Lingwise may provide certain features or functionalities of the Service on a paid basis.",
                        },
                        {
                            title: "7.2.",
                            content: "Available payment methods are determined by the Service Owner and may be changed without prior notice.",
                        },
                        {
                            title: "7.3.",
                            content: "For users located in the Russian Federation, payment for paid features is made directly to the Service Owner using third-party payment services, including but not limited to YooKassa. In this case, the Service Owner acts as the seller of the digital product.",
                        },
                        {
                            title: "7.4.",
                            content: "For users located outside the Russian Federation, payments may be processed by Paddle.com or another similar payment provider acting as Merchant of Record. In such cases, the relevant payment provider is the party to the sales agreement and determines applicable refund and tax rules.",
                        },
                        {
                            title: "7.5.",
                            content: "Subscriptions are automatically renewed unless cancelled by the User before the end of the paid period.",
                        },
                        {
                            title: "7.6.",
                            content: "Payments for access to digital functionality are non-refundable except where otherwise required by mandatory law or the rules of the applicable payment provider.",
                        },
                    ],
                },
                {
                    title: "8. Liability",
                    items: [
                        {
                            title: "8.1.",
                            content: "Lingwise is not liable for the inability to use the Service due to reasons beyond its control, including failures or limitations of Telegram, third-party services, platforms, APIs, payment providers, communication networks, hosting infrastructure, internet connectivity, or user devices.",
                        },
                        {
                            title: "8.2.",
                            content: "The Service is provided on an \"as is\" and \"as available\" basis, without any warranties, including warranties of uninterrupted, error-free, or continuous operation.",
                        },
                        {
                            title: "8.3.",
                            content: "Lingwise may suspend the operation of the Service in whole or in part for maintenance, updates, or other technical works without incurring liability to the User.",
                        },
                        {
                            title: "8.4.",
                            content: "Lingwise's total liability is limited to the amount actually paid by the User for paid features of the Service during the twelve (12) months preceding the relevant event.",
                        },
                    ],
                },
                {
                    title: "9. Indemnification",
                    items: [
                        {
                            title: "9.1.",
                            content: "The User agrees to independently settle and indemnify Lingwise against any third-party claims arising from the User's violation of these Terms.",
                        },
                    ],
                },
                {
                    title: "10. Links and Third-Party Services",
                    items: [
                        {
                            title: "10.1.",
                            content: "The Service may contain links or references to third-party resources. Lingwise does not control and is not responsible for their content or operation.",
                        },
                    ],
                },
                {
                    title: "11. Termination of Access",
                    items: [
                        {
                            title: "11.1.",
                            content: "Lingwise may restrict or terminate the User's access to the Service in the event of a violation of these Terms.",
                        },
                        {
                            title: "11.2.",
                            content: "Termination does not release the User from obligations incurred prior to termination.",
                        },
                    ],
                },
                {
                    title: "12. Privacy",
                    items: [
                        {
                            title: "12.1.",
                            content: "Information processing is governed by the Lingwise Privacy Policy.",
                        },
                    ],
                },
                {
                    title: "13. Dispute Resolution",
                    items: [
                        {
                            title: "13.1.",
                            content: "All disputes and disagreements shall be resolved through negotiations.",
                        },
                        {
                            title: "13.2.",
                            content: "If no agreement is reached, the dispute shall be resolved in accordance with the applicable laws of the Russian Federation.",
                        },
                    ],
                },
                {
                    title: "14. Final Provisions",
                    items: [
                        {
                            title: "14.1.",
                            content: "These Terms constitute the entire agreement between the User and Lingwise.",
                        },
                        {
                            title: "14.2.",
                            content: "Lingwise may assign its rights and obligations under these Terms to third parties.",
                        },
                        {
                            title: "14.3.",
                            content: "These Terms are governed by the laws of the Russian Federation.",
                        },
                    ],
                },
            ],
        },
        privacy: {
            title: "Lingwise Privacy Policy",
            lastUpdated: "Last updated: January 14, 2026",
            content: [
                "This Privacy Policy describes how information relating to users of the Lingwise service is processed when using the website and Telegram (the \"Service\").",
                "By using the Service, the user confirms their consent to the processing of information in accordance with this Privacy Policy.",
            ],
            sections: [
                {
                    title: "1. General Provisions",
                    items: [
                        {
                            title: "",
                            content: "This Privacy Policy governs the processing of information relating to users of the Lingwise Service available via the website and Telegram.",
                        },
                        {
                            title: "",
                            content: "By using the Service, the user confirms their consent to the terms of this Privacy Policy and agrees to the processing of information as described below.",
                        },
                        {
                            title: "",
                            content: "Certain features of the Service may be subject to additional privacy conditions, which will be disclosed to the user when such features are used.",
                        },
                    ],
                },
                {
                    title: "2. Scope of Information Processed",
                    items: [
                        {
                            title: "",
                            content: "Lingwise processes only the information that:",
                            subitems: [
                                "is transmitted by the user via the Telegram platform;",
                                "is provided by payment providers when paid features are used;",
                                "is generated automatically during use of the Service (technical and statistical data).",
                            ],
                        },
                        {
                            title: "",
                            content: "Lingwise does not intentionally collect special categories of personal data.",
                        },
                    ],
                },
                {
                    title: "3. Purposes and Legal Grounds for Processing",
                    items: [
                        {
                            title: "",
                            content: "Information is processed on the following legal grounds:",
                            subitems: [
                                "performance of the user agreement;",
                                "legitimate interests of Lingwise related to the development and protection of the Service;",
                                "compliance with applicable legal requirements;",
                                "user consent where required.",
                            ],
                        },
                        {
                            title: "",
                            content: "In particular, information may be used for:",
                            subitems: [
                                "operation and improvement of the Service;",
                                "personalization of learning content and storage of learning progress;",
                                "technical support and user communications;",
                                "analytics, testing, and quality improvement;",
                                "prevention of violations and misuse.",
                            ],
                        },
                    ],
                },
                {
                    title: "4. Transfer of Information to Third Parties",
                    items: [
                        {
                            title: "",
                            content: "Lingwise may engage third parties to support operation of the Service, including but not limited to:",
                            subitems: [
                                "hosting providers;",
                                "analytics and technical service providers;",
                                "payment systems and processing companies;",
                                "providers of artificial intelligence and automation technologies.",
                            ],
                        },
                        {
                            title: "",
                            content: "Information is shared only to the extent necessary to achieve the stated purposes.",
                        },
                        {
                            title: "",
                            content: "When paid features are used, payment information is processed by the relevant payment provider in accordance with its own privacy policy.",
                        },
                    ],
                },
                {
                    title: "5. Communications",
                    items: [
                        {
                            title: "",
                            content: "Lingwise may send users messages related to:",
                            subitems: [
                                "operation of the Service;",
                                "the learning process and progress;",
                                "changes to functionality or terms of use;",
                                "new features and offers.",
                            ],
                        },
                        {
                            title: "",
                            content: "Users may opt out of non-essential communications where such options are available through the platform used.",
                        },
                    ],
                },
                {
                    title: "6. Legal Compliance and Protection of Rights",
                    items: [
                        {
                            title: "",
                            content: "Information may be disclosed where necessary:",
                            subitems: [
                                "to comply with lawful requests of public authorities;",
                                "to protect the rights and legitimate interests of Lingwise or third parties;",
                                "to prevent fraud, violations, or harm.",
                            ],
                        },
                    ],
                },
                {
                    title: "7. Anonymized Data",
                    items: [
                        {
                            title: "",
                            content: "Lingwise may use aggregated and anonymized information without limitation. Such information does not allow identification of users and is not considered personal data.",
                        },
                    ],
                },
                {
                    title: "8. User Rights",
                    items: [
                        {
                            title: "",
                            content: "To the extent provided by applicable law, users have the right to:",
                            subitems: [
                                "obtain information about processed data;",
                                "request correction or deletion of data;",
                                "restrict or object to processing;",
                                "withdraw previously given consent;",
                                "request data portability.",
                            ],
                        },
                        {
                            title: "",
                            content: "Requests may be denied in cases permitted by law, including inability to identify the user or the need to retain data for legitimate purposes.",
                        },
                    ],
                },
                {
                    title: "9. Data Retention",
                    items: [
                        {
                            title: "",
                            content: "Information is retained for the duration of use of the Service and may be stored longer where necessary to:",
                            subitems: [
                                "comply with legal obligations;",
                                "protect the rights and interests of Lingwise;",
                                "resolve disputes;",
                                "ensure security and stability of the Service.",
                            ],
                        },
                        {
                            title: "",
                            content: "Aggregated data may be retained indefinitely.",
                        },
                    ],
                },
                {
                    title: "10. Cross-Border Processing",
                    items: [
                        {
                            title: "",
                            content: "Lingwise may process information using infrastructure located in various jurisdictions, subject to compliance with applicable data protection requirements.",
                        },
                    ],
                },
                {
                    title: "11. Third-Party Resources",
                    items: [
                        {
                            title: "",
                            content: "The Service may contain links to third-party websites or services. Lingwise does not control their privacy practices and is not responsible for their processing of information.",
                        },
                    ],
                },
                {
                    title: "12. Policy Updates",
                    items: [
                        {
                            title: "",
                            content: "Lingwise may update this Privacy Policy from time to time. The current version is always available on the Service website. Material changes take effect after a reasonable publication period.",
                        },
                    ],
                },
                {
                    title: "13. Contact Information",
                    items: [
                        {
                            title: "",
                            content: "For questions related to data processing and privacy, users may contact Lingwise at:",
                        },
                        {
                            title: "",
                            content: "lingwise@gmail.com",
                        },
                        {
                            title: "",
                            content: "Data controller (personal data operator):",
                        },
                        {
                            title: "",
                            content: "Vyacheslav Nikolaevich Endyushkin, self-employed individual (professional income tax regime, Russian Federation), Tax ID (INN): 732707619372.",
                        },
                    ],
                },
            ],
        },
        pricing: {
            title: "Pricing",
            description: "Lingwise is subscription-based. You can start for free.",
            free: {
                title: "Free",
                price: "$0",
                description: "Start and build a habit.",
                features: [
                    "Up to 100 words in active learning",
                    "Adaptive practice sessions",
                    "Pronunciation + example sentences",
                    "Basic progress overview",
                ],
                cta: "Start free",
            },
            paid: {
                title: "Paid",
                monthly: "Monthly",
                yearly: "Yearly",
                priceMonthly: "$4.99",
                priceYearly: "$39",
                perMonth: "/ month",
                perYear: "/ year",
                yearlyNote: "Yearly is better if you use Lingwise regularly.",
                description: "For long-term, focused work.",
                features: [
                    "Unlimited words in active learning (no limits)",
                    "Everything in Free",
                    "Full progress stats",
                    "\"Hard words\" mode / focused practice on difficult words",
                    "Text mode (send a text and extract words to learn)",
                ],
                cta: "Upgrade",
            },
            faq: {
                title: "Frequently asked questions",
                items: [
                    {
                        question: "Can I cancel anytime?",
                        answer: "Yes. You can cancel anytime.",
                    },
                    {
                        question: "Is there a commitment?",
                        answer: "No commitment.",
                    },
                    {
                        question: "Can I try before paying?",
                        answer: "Yes, Free plan is available.",
                    },
                ],
            },
        },
    },
};

export const t = (key: string, lang: Language): any => {
    const keys = key.split('.');
    let value: any = translations[lang];
    
    for (const k of keys) {
        value = value?.[k];
    }
    
    return value || key;
};
